import type { Extension, Timing } from '@medplum/fhirtypes';
import { z } from 'zod';

import { UCUM_SYSTEM } from './constants';
import type { Cadence } from './spec';

export const SESSION_FREQUENCY_EXTENSION_URL = `https://eluve.com/StructureDefinition/session-frequency`;

export const VISIT_OVERRIDE_EXTENSION_URL = `https://eluve.com/StructureDefinition/visit-override`;

const PERIOD_UNITS = ['d', 'wk', 'mo'] as const;
type PeriodUnit = (typeof PERIOD_UNITS)[number];

export const frequencySegmentSchema = z.object({
  frequency: z
    .number()
    .int()
    .min(1)
    .describe('Sessions per period, e.g. 2 for 2×/wk.'),
  period: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe(
      "Length of the period (default 1), e.g. 2 with periodUnit 'wk' = every 2 weeks.",
    ),
  periodUnit: z
    .enum(PERIOD_UNITS)
    .describe('Period unit: d(ay), wk (week), mo(nth).'),
  duration: z
    .number()
    .min(1)
    .optional()
    .describe(
      'How long this segment runs. Omit (with durationUnit) for an open-ended tail.',
    ),
  durationUnit: z
    .enum(PERIOD_UNITS)
    .optional()
    .describe(
      "Unit of `duration` — independent of periodUnit ('2×/mo for 2 months').",
    ),
});

export type FrequencySegment = z.infer<typeof frequencySegmentSchema>;

export type VisitOverride = {
  visit: number;
  date: string;
};

const UNIT_DAYS: Record<PeriodUnit, number> = { d: 1, wk: 7, mo: 30 };

function segmentToTiming(segment: FrequencySegment): Timing {
  return {
    repeat: {
      frequency: segment.frequency,
      period: segment.period ?? 1,
      periodUnit: segment.periodUnit,
      ...(segment.duration !== undefined && segment.durationUnit
        ? {
            boundsDuration: {
              value: segment.duration,
              unit: segment.durationUnit,
              system: UCUM_SYSTEM,
              code: segment.durationUnit,
            },
          }
        : {}),
    },
  };
}

function isPeriodUnit(value: unknown): value is PeriodUnit {
  return value === 'd' || value === 'wk' || value === 'mo';
}

function segmentFromTiming(
  timing: Timing | undefined,
): FrequencySegment | undefined {
  const repeat = timing?.repeat;
  if (!repeat?.frequency || !isPeriodUnit(repeat.periodUnit)) return undefined;
  const bounds = repeat.boundsDuration;
  const bounded = bounds?.value !== undefined && isPeriodUnit(bounds.code);
  return {
    frequency: repeat.frequency,
    ...(repeat.period !== undefined && repeat.period !== 1
      ? { period: repeat.period }
      : {}),
    periodUnit: repeat.periodUnit,
    ...(bounded
      ? { duration: bounds.value, durationUnit: bounds.code as PeriodUnit }
      : {}),
  };
}

export function sessionFrequencyExtension(
  segments: FrequencySegment[],
): Extension {
  return {
    url: SESSION_FREQUENCY_EXTENSION_URL,
    extension: segments.map((segment) => ({
      url: 'segment',
      valueTiming: segmentToTiming(segment),
    })),
  };
}

export function sessionFrequencyOf(resource: {
  extension?: Extension[];
}): FrequencySegment[] | undefined {
  const ext = resource.extension?.find(
    (e) => e.url === SESSION_FREQUENCY_EXTENSION_URL,
  );
  if (!ext) return undefined;
  const segments = (ext.extension ?? [])
    .filter((e) => e.url === 'segment')
    .map((e) => segmentFromTiming(e.valueTiming))
    .filter((s): s is FrequencySegment => s !== undefined);
  return segments.length > 0 ? segments : undefined;
}

export function withSessionFrequency<T extends { extension?: Extension[] }>(
  resource: T,
  segments: FrequencySegment[],
): T {
  const others = (resource.extension ?? []).filter(
    (e) => e.url !== SESSION_FREQUENCY_EXTENSION_URL,
  );
  return {
    ...resource,
    extension: [...others, sessionFrequencyExtension(segments)],
  };
}

function overrideOf(ext: Extension): VisitOverride | undefined {
  const visit = ext.extension?.find((e) => e.url === 'visit')?.valueInteger;
  const date = ext.extension?.find((e) => e.url === 'date')?.valueDate;
  if (visit === undefined || !date) return undefined;
  return { visit, date };
}

export function visitOverridesOf(resource: {
  extension?: Extension[];
}): VisitOverride[] {
  return (resource.extension ?? [])
    .filter((e) => e.url === VISIT_OVERRIDE_EXTENSION_URL)
    .map(overrideOf)
    .filter((o): o is VisitOverride => o !== undefined)
    .sort((a, b) => a.visit - b.visit);
}

export function upsertVisitOverride(
  extensions: Extension[] | undefined,
  override: VisitOverride,
): Extension[] {
  const kept = (extensions ?? []).filter(
    (e) =>
      e.url !== VISIT_OVERRIDE_EXTENSION_URL ||
      overrideOf(e)?.visit !== override.visit,
  );
  return [
    ...kept,
    {
      url: VISIT_OVERRIDE_EXTENSION_URL,
      extension: [
        { url: 'visit', valueInteger: override.visit },
        { url: 'date', valueDate: override.date },
      ],
    },
  ];
}

export function removeVisitOverride(
  extensions: Extension[] | undefined,
  visit: number,
): Extension[] {
  return (extensions ?? []).filter(
    (e) =>
      e.url !== VISIT_OVERRIDE_EXTENSION_URL || overrideOf(e)?.visit !== visit,
  );
}

export function segmentIntervalDays(segment: FrequencySegment): number {
  return (
    (UNIT_DAYS[segment.periodUnit] * (segment.period ?? 1)) / segment.frequency
  );
}

export function segmentVisitCount(segment: FrequencySegment): number | null {
  if (segment.duration === undefined || !segment.durationUnit) return null;
  const durationDays = UNIT_DAYS[segment.durationUnit] * segment.duration;
  return Math.max(1, Math.round(durationDays / segmentIntervalDays(segment)));
}

export function totalVisitCount(segments: FrequencySegment[]): number | null {
  let total = 0;
  for (const segment of segments) {
    const count = segmentVisitCount(segment);
    if (count === null) return null;
    total += count;
  }
  return total;
}

function unitLabel(unit: PeriodUnit, value: number): string {
  if (unit === 'd') return value === 1 ? 'day' : 'days';
  if (unit === 'wk') return value === 1 ? 'wk' : 'wks';
  return 'mo';
}

export function formatSegmentCadence(segment: FrequencySegment): string {
  const period = segment.period ?? 1;
  const unit = unitLabel(segment.periodUnit, period);
  return period === 1
    ? `${segment.frequency}×/${unit}`
    : `${segment.frequency}×/${period} ${unit}`;
}

export function formatSegment(segment: FrequencySegment): string {
  const cadence = formatSegmentCadence(segment);
  if (segment.duration === undefined || !segment.durationUnit)
    return `${cadence} ongoing`;
  return `${cadence} × ${segment.duration} ${unitLabel(
    segment.durationUnit,
    segment.duration,
  )}`;
}

export function formatFrequencyHeadline(segments: FrequencySegment[]): string {
  const total = totalVisitCount(segments);
  const label = total === null ? 'Ongoing' : `${total} visits`;
  return `${label} · ${segments.map(formatSegment).join(' → ')}`;
}

function normalizeDuration(days: number): {
  duration: number;
  durationUnit: PeriodUnit;
} {
  if (days % 30 === 0) return { duration: days / 30, durationUnit: 'mo' };
  if (days % 7 === 0) return { duration: days / 7, durationUnit: 'wk' };
  return { duration: days, durationUnit: 'd' };
}

export function segmentsFromPhases(
  phases: {
    cadence?: Cadence;
    durationDays?: number;
    visitsToComplete?: number;
  }[],
): FrequencySegment[] | undefined {
  const segments: FrequencySegment[] = [];
  for (const phase of phases) {
    if (phase.cadence) {
      segments.push({
        frequency: phase.cadence.frequency,
        ...(phase.cadence.period !== undefined && phase.cadence.period !== 1
          ? { period: phase.cadence.period }
          : {}),
        periodUnit: phase.cadence.periodUnit,
        ...(phase.durationDays !== undefined && phase.durationDays > 0
          ? normalizeDuration(phase.durationDays)
          : {}),
      });
    } else if (
      phase.visitsToComplete !== undefined &&
      phase.durationDays !== undefined &&
      phase.durationDays > 0
    ) {
      const { duration, durationUnit } = normalizeDuration(phase.durationDays);
      segments.push({
        frequency: phase.visitsToComplete,
        ...(duration !== 1 ? { period: duration } : {}),
        periodUnit: durationUnit,
        duration,
        durationUnit,
      });
    }
  }
  return segments.length > 0 ? segments : undefined;
}
